import { memo } from "react";

import { Video } from "@shopify/hydrogen-react";
import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { Badge, type BadgeType } from "@components/Badge";
import { Image } from "@components/Image";
import {
  Carousel,
  Track,
  Slide,
  CarouselPaginationDots,
  onPaginationUpdated as onCarouselPaginationUpdated,
} from "@react/components/Carousel";
import { PAGE_CAROUSEL_OPTIONS } from "@react/components/Carousel/constants";
import { ProductVideoModalButton } from "@sections/ProductHero/ProductVideoModalButton";
import {
  type CartMediaVideo,
  type CartMediaImage,
  type ProductHeroSettings,
} from "@tablescape/state";
import { tw } from "classnames";

const productMediaCarouselOptions = {
  ...PAGE_CAROUSEL_OPTIONS,
  arrows: false,
  gap: 0,
  pagination: true,
};

type Props = {
  alternateImage?: { alt: string; src: string };
  badgeText: Maybe<string>;
  badgeType: Maybe<BadgeType>;
  carouselAria?: {
    "aria-describedby"?: string;
    "aria-label"?: string;
    "aria-roledescription": string;
    carouselId: string;
  };
  isLCPRender: boolean;
  lcpMedia: ProductHeroSettings["productGalleryCarousel"]["lcpMedia"];
  medias: (CartMediaImage | CartMediaVideo)[];
  videoAriaLabel: string;
};

const styles = {
  base: tw("overflow-hidden", "relative"),
  slide: tw("w-full"),
  media: tw(
    "flex",
    "items-center",
    "justify-center",
    "overflow-hidden",
    "w-full"
  ),
  image: tw("flex", "w-full", "h-auto"),
  paginationDots: tw("absolute", "bottom-8", "right-6"),
  video: tw("block", "h-full", "w-full", "object-cover"),
  badge: tw("absolute", "md:top-8", "md:left-8", "top-4", "left-6"),
  videoModalButton: tw("absolute", "bottom-6", "left-6"),
};

const ProductGalleryGridViewBase = ({
  alternateImage,
  badgeText,
  badgeType,
  carouselAria,
  isLCPRender,
  lcpMedia,
  medias,
  videoAriaLabel,
}: Props) => {
  return (
    <div className={styles.base}>
      <Carousel
        hasTrack={false}
        onPaginationUpdated={onCarouselPaginationUpdated}
        options={{
          ...productMediaCarouselOptions,
          pagination: medias.length > 1,
        }}
        {...(carouselAria
          ? {
              "aria-label": carouselAria["aria-label"],
              "aria-roledescription": carouselAria["aria-roledescription"],
              "aria-describedby": carouselAria["aria-describedby"],
            }
          : {})}
      >
        <Track>
          {medias.map((media, index) => (
            <Slide
              className={styles.slide}
              key={`pdpGalleryCarousel-media-${index}`}
            >
              <div className={styles.media}>
                {media.mediaContentType === "IMAGE" ? (
                  index === 0 ? (
                    isLCPRender ? (
                      <img
                        alt={lcpMedia.altText}
                        className={tw(
                          "flex",
                          "w-full",
                          "h-auto",
                          "aspect-square"
                        )}
                        decoding="async"
                        // eslint-disable-next-line react/no-unknown-property
                        fetchpriority="high"
                        height="767"
                        loading="eager"
                        src={lcpMedia.sources[0].src}
                        srcSet={lcpMedia.sources
                          .map(
                            ({ devicePixelRatio, src }) =>
                              `${src} ${devicePixelRatio}x`
                          )
                          .join(", ")}
                        width="767"
                      />
                    ) : (
                      <Image
                        alt={
                          index === 0
                            ? alternateImage?.alt ?? media.image.altText ?? ""
                            : media.image.altText ?? ""
                        }
                        className={styles.image}
                        height={767}
                        loading="eager"
                        src={
                          index === 0
                            ? alternateImage?.src ?? media.image.url
                            : media.image.url
                        }
                        width={767}
                      />
                    )
                  ) : (
                    <Image
                      alt={media.image.altText ?? ""}
                      className={styles.image}
                      fetchpriority="low"
                      height={767}
                      loading="lazy"
                      src={media.image.url}
                      width={767}
                    />
                  )
                ) : (
                  <Video
                    aria-label={videoAriaLabel}
                    autoPlay
                    className={styles.video}
                    controls={false}
                    data={{
                      mediaContentType: "VIDEO",
                      sources: media.sources,
                    }}
                    data-chromatic="ignore"
                    height={767}
                    loop
                    muted
                    playsInline
                    preload="none"
                    width={767}
                  />
                )}
                {index === 0 && !!badgeText && (
                  <div className={styles.badge}>
                    <Badge
                      size={{ sm: "md", md: "lg" }}
                      type={badgeType ?? undefined}
                    >
                      {badgeText}
                    </Badge>
                  </div>
                )}
                {index === 0 && (
                  <div className={styles.videoModalButton}>
                    <ProductVideoModalButton size="small" />
                  </div>
                )}
              </div>
            </Slide>
          ))}
        </Track>

        <CarouselPaginationDots
          carouselId={carouselAria?.carouselId}
          className={styles.paginationDots}
        />
      </Carousel>
    </div>
  );
};

export const ProductGalleryCarouselView = memo(ProductGalleryGridViewBase);
